import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Box,
  Dialog,
  DialogContent,
  Tab,
  Tabs,
  Table,
  TableContainer,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  AccountCircle as AccountCircleIcon,
  CheckCircle as SuccessIcon,
  RemoveCircle as PartialIcon,
  Error as FailureIcon,
} from '@material-ui/icons';
import {
  red,
  yellow,
  green,
  grey,
} from '@material-ui/core/colors';
import BaseButton from '../common/BaseButton';
import StatusIcon from '../common/StatusIcon';
import MatchTooltip from '../common/MatchTooltip';

import {
  selectCard,
  selectGuest,
  selectCompany,
  selectCompanyCode,
  selectReservationCode,
  getPaymentCardSessionByReservation,
  createPaymentCardSession,
} from '../../store/widgetSlice';

import {
  setMessage,
} from '../../store/authSlice';

const useStyles = makeStyles((theme) => ({
  container: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    width: 450,
    maxWidth: '100%',
  },
  panel: {
    backgroundColor: theme.palette.background.default,
  },
  status: {
    fontWeight: 600,
  },
  details: {
    maxWidth: 260,
    fontSize: 14,
    marginBottom: theme.spacing(2),
    textAlign: 'center',
  },
  selfie: {
    width: 150,
    height: 150,
    borderRadius: '50%',
    objectFit: 'cover',
    objectPosition: 'center',
  },
  document: {
    width: 200,
    height: 150,
    objectFit: 'cover',
    objectPosition: 'center',
    maxWidth: '100%',
  },
  bold: {
    fontWeight: 600,
  },
  cell: {
    height: 30,
    padding: 0,
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),

    'tr:last-child &': {
      borderBottom: 0,
    },
  },
  badge: {
    position: 'absolute',
    top: -theme.spacing(1),
    right: -theme.spacing(1),
    borderRadius: '50%',
    background: theme.palette.background.paper,
  },
  cardImage: {
    width: '100%',
  },
}));

function compareNames(name1, name2) {
  return typeof name1 === 'string'
    && typeof name2 === 'string'
    && name1.toLowerCase() === name2.toLowerCase();
}

function TabPanel(props) {
  const {
    children,
    value,
    index,
    ...other
  } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box>
          <Typography>{children}</Typography>
        </Box>
      )}
    </div>
  );
}

function VerificationStatus({ classes }) {
  const dispatch = useDispatch();
  const session = useSelector(selectCard);
  const companyCode = useSelector(selectCompanyCode);
  const reservationCode = useSelector(selectReservationCode);

  const handleClick = ((event) => {
    event.preventDefault();

    if (companyCode && reservationCode) {
      dispatch(createPaymentCardSession({ companyCode, reservationCode }))
        .unwrap()
        .then(() => {
          dispatch(setMessage({ message: 'Session created', severity: 'success' }));
        })
        .catch(() => {
          dispatch(setMessage({ message: 'Session could not be created', severity: 'error' }));
        });
    }
  });

  if (!session) {
    return (
      <Box align="center">
        <Box mt={6}>
          <Typography className={classes.status} component="h3" variant="h6">
            Card verification has not been started
          </Typography>
        </Box>
        <Box my={6}>
          <PartialIcon style={{ color: grey[500], fontSize: 80 }} />
          <Typography className={classes.details} component="p" variant="body1">
            An email and SMS message will be sent to the guest with a verification link.
          </Typography>
        </Box>
        <Box my={6}>
          <BaseButton
            IconComponent={AccountCircleIcon}
            onClick={handleClick}
          >
            Begin Verification
          </BaseButton>
        </Box>
      </Box>
    );
  }

  if (session && session.status && (session.status === 'P' || session.status === 'R' || session.status === 'A')) {
    return (
      <Box align="center">
        <Box mt={6}>
          <Typography className={classes.status} component="h3" variant="h6">
            Card verification is pending
          </Typography>
        </Box>
        <Box my={6}>
          <PartialIcon style={{ color: yellow[500], fontSize: 80 }} />
          <Typography className={classes.details} component="p" variant="body1">
            A verification link has been sent to the guest.
          </Typography>
        </Box>
        <Box my={6}>
          <BaseButton
            IconComponent={AccountCircleIcon}
            onClick={handleClick}
          >
            Retry Verification
          </BaseButton>
        </Box>
      </Box>
    );
  }

  if (session && session.status === 'C') {
    return (
      <Box align="center">
        <Box mt={6}>
          <Typography className={classes.status} component="h3" variant="h6">
            Card verification has been completed
          </Typography>
        </Box>
        <Box my={6}>
          <SuccessIcon style={{ color: green[500], fontSize: 80 }} />
          <Typography className={classes.details} component="p" variant="body1">
            Guest information matches the payment card provided.
          </Typography>
        </Box>
        <Box my={6}>
          <BaseButton
            IconComponent={AccountCircleIcon}
            onClick={handleClick}
          >
            Retry Verification
          </BaseButton>
        </Box>
      </Box>
    );
  }

  return (
    <Box align="center">
      <Box mt={6}>
        <Typography align="center" className={classes.status} component="h3" variant="h6">
          Card verification has been completed
        </Typography>
      </Box>
      <Box my={6}>
        <FailureIcon style={{ color: red[500], fontSize: 80 }} />
        <Typography align="center" className={classes.details} component="p" variant="body1">
          Card information does not match scan. Go to the Details tab for more information.
        </Typography>
      </Box>
      <Box my={6}>
        <BaseButton
          IconComponent={AccountCircleIcon}
          onClick={handleClick}
        >
          Retry Verification
        </BaseButton>
      </Box>
    </Box>
  );
}

export default function CardModal({
  compact,
  hideDefaultButton,
  externalOpen,
  externalHandleClose,
}) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const guest = useSelector(selectGuest);
  const company = useSelector(selectCompany);
  const companyCode = useSelector(selectCompanyCode);
  const reservationCode = useSelector(selectReservationCode);
  const session = useSelector(selectCard);

  const [open, setOpen] = React.useState(false);
  const [currentTab, setCurrentTab] = React.useState(0);

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  useEffect(() => {
    if (companyCode && reservationCode) {
      dispatch(getPaymentCardSessionByReservation({ companyCode, reservationCode }));
    }
  }, [dispatch, companyCode, reservationCode]);

  useEffect(() => {
    setOpen(externalOpen);
  }, [externalOpen]);

  return (
    <div>
      {
        !hideDefaultButton
        && (
          <button className="grwidget__status-button" type="button" onClick={handleClickOpen}>
            <div className="grwidget__status-item">
              <span className="grwidget__status-label">Payment Card</span>
              <span className="grwidget__status-icon">
                <StatusIcon status={guest && guest.cardVerified} />
              </span>
            </div>
          </button>
        )
      }
      <Dialog
        className={classes.dialog}
        open={open}
        onClose={externalHandleClose || handleClose}
        maxWidth="sm"
      >
        <Paper>
          <Tabs
            value={currentTab}
            indicatorColor="primary"
            textColor="primary"
            onChange={handleTabChange}
            variant="fullWidth"
          >
            <Tab label="Verification Status" />
            <Tab
              label="Details"
              disabled={!session || (session.status === 'P' || session.status === 'R' || session.status === 'A')}
            />
          </Tabs>
        </Paper>
        <DialogContent>
          <TabPanel className={classes.panel} value={currentTab} index={0}>
            <VerificationStatus classes={classes} />
          </TabPanel>
          <TabPanel
            className={classes.panel}
            value={currentTab}
            index={1}
          >
            <Box m={3}>
              {
                session && session.cardImages && (session.cardImages.edited || session.cardImages.original)
                && (
                  <div className={classes.cardImageContainer}>
                    <img
                      className={classes.cardImage}
                      src={`data:image/png;base64,${session.cardImages.edited || session.cardImages.original}`}
                      alt={session.reference}
                    />
                  </div>
                )
              }
              <TableContainer className={classes.container}>
                <Table className={classes.table}>
                  <TableBody>
                    <TableRow className={classes.row}>
                      <TableCell className={[classes.cell, classes.bold]} align="left">
                        Reference No.
                      </TableCell>
                      <TableCell className={classes.cell} align="right">
                        {session && session.reference}
                      </TableCell>
                      <TableCell className={classes.cell} align="right" />
                    </TableRow>
                    {
                    (!compact)
                      ? (
                        <>
                          <TableRow className={classes.row}>
                            <TableCell className={[classes.cell, classes.bold]} align="left">
                              First Name
                            </TableCell>
                            <TableCell className={classes.cell} align="right">
                              {session && session.firstName}
                            </TableCell>
                            <TableCell className={classes.cell} align="right">
                              <MatchTooltip
                                guest={guest && guest.firstName}
                                match={session && session.firstName}
                              >
                                <StatusIcon
                                  className={classes.tableIcon}
                                  status={(guest && session
                              && (compareNames(guest.firstName, session.firstName))) ? 3 : 1}
                                />
                              </MatchTooltip>
                            </TableCell>
                          </TableRow>
                          <TableRow className={classes.row}>
                            <TableCell className={[classes.cell, classes.bold]} align="left">
                              Last Name
                            </TableCell>
                            <TableCell className={classes.cell} align="right">
                              {session && session.lastName}
                            </TableCell>
                            <TableCell className={classes.cell} align="right">
                              <MatchTooltip
                                guest={guest && guest.lastName}
                                match={session && session.lastName}
                              >
                                <StatusIcon
                                  className={classes.tableIcon}
                                  status={(guest && session
                              && compareNames(guest.lastName, session.lastName)) ? 3 : 1}
                                />
                              </MatchTooltip>
                            </TableCell>
                          </TableRow>
                          <TableRow className={classes.row}>
                            <TableCell className={[classes.cell, classes.bold]} align="left">
                              Card Network
                            </TableCell>
                            <TableCell className={classes.cell} align="right">
                              {session && session.cardNetwork}
                            </TableCell>
                            <TableCell className={classes.cell} align="right" />
                          </TableRow>
                        </>
                      )
                      : <></>
                    }
                    <TableRow className={classes.row}>
                      <TableCell className={[classes.cell, classes.bold]} align="left">
                        Card Number
                      </TableCell>
                      <TableCell className={classes.cell} align="right">
                        {session && session.cardNumber}
                      </TableCell>
                      { company && company.customAttributes && company.customAttributes.cardCheck
                        && company.customAttributes.cardCheck !== 'OFF'
                        && (
                        <TableCell className={classes.cell} align="right">
                          <MatchTooltip
                            guest={guest && guest.cardNumber}
                            match={session && session.cardNumber}
                          >
                            <StatusIcon
                              className={classes.tableIcon}
                              status={(guest && session
                                && (guest.cardNumber === session.cardNumber)) ? 3 : 1}
                            />
                          </MatchTooltip>
                        </TableCell>
                        )}
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </TabPanel>
        </DialogContent>
      </Dialog>
    </div>
  );
}
